import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Rocket, Sparkles } from 'lucide-react';
import { formatNumber } from '@/lib/format';
import { Popover, PopoverContent, PopoverTrigger, cn, focusRing } from '@/ui';
import { compact } from './goalModel';
import { useRideGoal, type GoalToast, type RideGoalState } from './RideGoalProvider';

/** Counts up to `value` (about a second), or jumps there under reduced motion. */
function useTicker(value: number): number {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current;
    from.current = value;
    if (start === value) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || start === 0) {
      setShown(value);
      return;
    }
    const began = performance.now();
    let frame = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - began) / 900);
      setShown(Math.round(start + (value - start) * (1 - (1 - k) ** 3)));
      if (k < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return shown;
}

/** The bar itself: progress fill and a tick where the even pace to the target is today. */
function ProgressBar({ pct, pacePct, className }: { pct: number; pacePct: number; className?: string }) {
  return (
    <span className={cn('relative block h-1.5 overflow-hidden rounded-full bg-muted', className)} aria-hidden>
      <span
        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent to-brand transition-[width] duration-1000 ease-out"
        style={{ width: `${pct}%` }}
      />
      <span className="absolute inset-y-[-2px] w-0.5 rounded-full bg-heading/60" style={{ left: `${pacePct}%` }} />
    </span>
  );
}

function PaceLine({ goal, className }: { goal: RideGoalState; className?: string }) {
  const { t, i18n } = useTranslation('shell', { keyPrefix: 'goal' });
  const n = compact(Math.abs(goal.paceDelta), i18n.language);
  const close = Math.abs(goal.paceDelta) < goal.target * 0.001;
  return (
    <span className={cn('font-semibold', close || goal.paceDelta >= 0 ? 'text-success-ink' : 'text-warning-ink', className)}>
      {close ? t('onPace') : goal.paceDelta > 0 ? t('ahead', { n }) : t('behind', { n })}
    </span>
  );
}

/**
 * Mission strip under the header, on every page: year-to-date rides of every market against the
 * goal, the even-pace tick and how far ahead or behind it is. Opens the goal's details; live
 * toasts drop from it as markets add rides.
 */
export function GoalBar() {
  const goal = useRideGoal();
  const { t, i18n } = useTranslation('shell', { keyPrefix: 'goal' });
  const total = useTicker(goal?.total ?? 0);
  if (!goal) return null;
  const target = compact(goal.target, i18n.language);
  return (
    <div className="relative z-20 shrink-0 border-b border-line bg-subtle">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t('aria', { year: goal.year, done: formatNumber(goal.total), target: formatNumber(goal.target), pct: goal.pct })}
            className={cn(
              'm-0 flex h-8 w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-3 text-left font-sans text-xs text-ink-muted hover:bg-accent-softer sm:px-4',
              focusRing,
            )}
          >
            <span className="flex shrink-0 items-center gap-1.5 font-bold uppercase tracking-wide text-heading">
              <Rocket size={13} aria-hidden className="text-accent" />
              <span>{t('mission', { year: goal.year })}</span>
              <span className="hidden font-normal normal-case tracking-normal text-ink-muted sm:inline">· {t('target', { target })}</span>
            </span>
            <ProgressBar pct={goal.pct} pacePct={goal.pacePct} className="min-w-16 flex-1" />
            <span className="shrink-0 tabular-nums">
              <span className="font-bold text-heading">{goal.counting ? '…' : compact(total, i18n.language)}</span>
              <span className="hidden sm:inline"> / {target}</span> · {goal.pct}%
            </span>
            {!goal.counting && <PaceLine goal={goal} className="hidden shrink-0 md:inline" />}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[22rem] max-w-[calc(100vw-1.5rem)] p-0" aria-label={t('mission', { year: goal.year })}>
          <GoalDetails goal={goal} />
        </PopoverContent>
      </Popover>
      <GoalToastView goal={goal} />
    </div>
  );
}

/**
 * One market's new rides (or a milestone) at a time, bottom-left of the screen; never takes focus, read
 * out politely, and without motion under reduced motion.
 */
function GoalToastView({ goal }: { goal: RideGoalState }) {
  const { t, i18n } = useTranslation('shell', { keyPrefix: 'goal' });
  const toast: GoalToast | null = goal.toast;
  return (
    // bottom-left, clear of every page's header actions (they sat over Resume, Edit …) and of
    // the assistant button bottom-right
    <div role="status" aria-live="polite" className="pointer-events-none fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-3 z-40 flex sm:bottom-4 sm:left-4">
      {toast && (
        <div
          key={toast.id}
          className={cn(
            'pointer-events-auto relative w-72 max-w-[calc(100vw-1.5rem)] animate-goal-in overflow-hidden rounded-lg border bg-surface px-3.5 py-3 shadow-pop',
            toast.kind === 'milestone' ? 'border-accent' : 'border-line',
          )}
          onClick={goal.dismissToast}
        >
          {toast.kind === 'milestone' && <Sparkle />}
          <p className="m-0 font-display text-sm font-semibold text-heading">
            {toast.kind === 'rides' ? (
              <>
                <span className="text-accent">{toast.market}</span> {t('toast', { count: toast.count })}
              </>
            ) : (
              t('milestone', { value: compact(toast.value, i18n.language) })
            )}
          </p>
          <p className="m-0 mt-0.5 text-xs text-ink-muted">
            {toast.kind === 'milestone' && toast.leader ? t('leads', { market: toast.leader }) : t('toward', { target: compact(goal.target, i18n.language) })}
          </p>
          <div className="mt-2 flex items-center gap-2 text-xs tabular-nums text-ink-muted">
            <ProgressBar pct={goal.pct} pacePct={goal.pacePct} className="flex-1" />
            <span className="font-bold text-heading">{goal.pct}%</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** A few stars around a milestone toast (a single short burst). */
function Sparkle() {
  const stars = [
    { left: '8%', top: '18%', delay: '0ms' },
    { left: '88%', top: '14%', delay: '180ms' },
    { left: '70%', top: '70%', delay: '320ms' },
    { left: '30%', top: '76%', delay: '460ms' },
  ];
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      <span className="absolute inset-0 bg-gradient-to-r from-accent-soft/0 via-accent-soft/70 to-accent-soft/0" />
      {stars.map((s) => (
        <Sparkles
          key={s.left}
          size={12}
          className="absolute animate-twinkle text-brand opacity-0"
          style={{ left: s.left, top: s.top, animationDelay: s.delay }}
        />
      ))}
    </span>
  );
}

/** The goal in full: progress, pace, today's leading markets and each market's share of the goal. */
export function GoalDetails({ goal, limit = 5 }: { goal: RideGoalState; limit?: number }) {
  const { t, i18n } = useTranslation('shell', { keyPrefix: 'goal' });
  const target = compact(goal.target, i18n.language);
  const top = goal.markets.slice(0, limit);
  const others = goal.markets.slice(limit).reduce((n, m) => n + m.rides, 0);
  const share = (n: number) => `${(Math.floor((n / goal.target) * 1000) / 10).toLocaleString(i18n.language)}%`;
  return (
    <section aria-label={t('mission', { year: goal.year })} className="flex flex-col gap-4 px-4 py-4">
      <div>
        <h3 className="m-0 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-muted">
          <Rocket size={13} aria-hidden className="text-accent" />
          {t('mission', { year: goal.year })}
        </h3>
        <p className="m-0 mt-1.5 font-display text-2xl font-bold tabular-nums text-heading">
          {goal.counting ? t('counting') : compact(goal.total, i18n.language)}
          <span className="text-base font-semibold text-ink-muted"> / {t('target', { target })}</span>
        </p>
        <ProgressBar pct={goal.pct} pacePct={goal.pacePct} className="mt-2 h-2" />
        <p className="m-0 mt-1.5 flex justify-between text-xs">
          <span className="tabular-nums text-ink-muted">{goal.pct}%</span>
          {!goal.counting && <PaceLine goal={goal} />}
        </p>
      </div>
      {goal.today.length > 0 && (
        <div>
          <h4 className="m-0 mb-2 text-xs font-bold uppercase tracking-wide text-ink-muted">{t('todayLeaders')}</h4>
          <ol className="m-0 flex list-none flex-col gap-1 p-0">
            {goal.today.slice(0, limit).map((m, i) => (
              <li key={m.name} className="grid grid-cols-[1.25rem_1fr_auto_1rem] items-center gap-2 text-xs">
                <span className="tabular-nums text-ink-faint">{i + 1}</span>
                <span className="truncate text-ink">{m.name}</span>
                <span className="tabular-nums font-semibold text-heading">{formatNumber(m.today)}</span>
                <span aria-label={m.move > 0 ? t('movedUp') : m.move < 0 ? t('movedDown') : undefined}>
                  {m.move > 0 ? (
                    <ArrowUp size={12} className="text-success-ink" aria-hidden />
                  ) : m.move < 0 ? (
                    <ArrowDown size={12} className="text-ink-faint" aria-hidden />
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
      {top.length > 0 && (
        <div>
          <h4 className="m-0 mb-2 text-xs font-bold uppercase tracking-wide text-ink-muted">{t('contribution', { target })}</h4>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {[...top, ...(others > 0 ? [{ name: t('others'), rides: others }] : [])].map((m) => (
              <li key={m.name} className="grid grid-cols-[6rem_1fr_auto] items-center gap-2 text-xs">
                <span className="truncate text-ink">{m.name}</span>
                <span className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <span className="block h-full rounded-full bg-brand" style={{ width: `${Math.max(1, (m.rides / (top[0]?.rides || 1)) * 100)}%` }} />
                </span>
                <span className="tabular-nums text-ink-muted">
                  {compact(m.rides, i18n.language)} · {share(m.rides)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
